import {
  ChevronLeft,
  ChevronRight,
  Settings,
  LayoutDashboard,
  FileText,
  FileCheck,
  Users,
  Package,
  X,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Logo from "../components/Logo";
import { useSidebarToggle } from "../hooks/useSidebarToggle";

const items = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
  { key: "quotations", label: "Quotations", icon: FileCheck, path: "/quotations" },
  { key: "invoices", label: "Invoices", icon: FileText, path: "/invoices" },
  { key: "clients", label: "Clients", icon: Users, path: "/clients" },
  { key: "products", label: "Products", icon: Package, path: "/products" },
];

export default function Sidebar() {
  const { isCollapsed, toggleCollapsed, isMobileOpen, closeMobile } = useSidebarToggle();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine active item based on current path
  const getActiveKey = () => {
    const path = location.pathname;
    const item = items.find(i => i.path === path);
    return item?.key || "dashboard";
  };
  
  const active = getActiveKey();

  // Close mobile sidebar on route change
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  // Handle navigation
  const handleNavigate = (path) => {
    navigate(path);
    closeMobile(); // Close mobile drawer after navigation
  };

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          /* Desktop Styles (unchanged) */
          lg:h-[calc(100vh-24px)]
          lg:m-3
          lg:rounded-xl
          lg:relative
          lg:translate-x-0
          
          /* Mobile Styles (new) */
          fixed lg:static
          inset-y-0 left-0
          z-50 lg:z-auto
          h-full lg:h-[calc(100vh-24px)]
          
          /* Shared Styles */
          bg-[var(--color-bg-canvas)]
          border-r lg:border border-[var(--color-border)]
          shadow-2xl lg:shadow-lg
          transition-all duration-300 ease-in-out
          
          /* Width & Transform */
          ${isCollapsed ? "lg:w-[80px]" : "lg:w-[260px]"}
          w-[280px] lg:w-auto
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          
          flex flex-col
        `}
      >
        {/* Mobile Close Button */}
        <button
          onClick={closeMobile}
          className="lg:hidden absolute top-4 right-4 z-10 w-8 h-8 rounded-lg bg-[var(--color-bg-muted)] hover:bg-[var(--color-bg-canvas)] border border-[var(--color-border)] flex items-center justify-center transition-colors cursor-pointer"
          aria-label="Close sidebar"
        >
          <X size={16} />
        </button>

        {/* Desktop Toggle Button - Hidden on mobile */}
        <button
          onClick={toggleCollapsed}
          className={`
            hidden lg:flex
            absolute top-6 -right-3 z-10
            w-8 h-8
            rounded-full 
            bg-[var(--color-bg-canvas)] 
            border border-[var(--color-border)]
            shadow-md
            items-center justify-center
            hover:bg-[var(--color-bg-muted)]
            transition-all duration-200
            cursor-pointer
          `}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        {/* Logo */}
        <div className="flex items-center justify-center px-4 py-6 lg:py-6 pt-16 lg:pt-6">
          {!isCollapsed && (
            <div className="flex items-center justify-center w-full">
              <Logo size="horizontal-xl" variant="horizontal" theme="transparent" />
            </div>
          )}
          {isCollapsed && (
            <div className="hidden lg:flex justify-center w-full">
              <Logo size="xl" variant="icon" theme="transparent" />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 lg:px-2 space-y-1 overflow-y-auto">
          {items.map(({ key, label, icon: Icon, path }) => {
            const isActive = active === key;

            return (
              <button
                key={key}
                onClick={() => handleNavigate(path)}
                className={`
                  w-full
                  flex items-center gap-3
                  lg:${isCollapsed ? 'justify-center' : 'gap-3'}
                  px-4 lg:px-3 
                  py-3 lg:py-2
                  rounded-lg
                  text-sm font-medium
                  transition-all duration-200
                  min-h-[44px] lg:min-h-0
                  cursor-pointer
                  ${
                    isActive
                      ? "bg-[rgba(79,70,229,0.08)] text-[var(--color-primary)] shadow-sm"
                      : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)] active:scale-[0.98]"
                  }
                `}
              >
                <Icon size={20} className="lg:w-[18px] lg:h-[18px] shrink-0" />
                <span className={`${isCollapsed ? 'lg:hidden' : ''}`}>{label}</span>
              </button>
            );
          })}
        </nav>

        {/* Settings */}
        <div className="px-3 lg:px-2 py-4 lg:py-3 border-t border-[var(--color-border)]">
          <button
            onClick={() => handleNavigate("/settings")}
            className={`
              w-full
              flex items-center gap-3
              lg:${isCollapsed ? 'justify-center' : 'gap-3'}
              px-4 lg:px-3 
              py-3 lg:py-2
              rounded-lg 
              text-sm 
              text-[var(--color-text-secondary)]
              hover:bg-[var(--color-bg-muted)]
              transition-all duration-200
              min-h-[44px] lg:min-h-0
              active:scale-[0.98]
              cursor-pointer
            `}
          >
            <Settings size={20} className="lg:w-[18px] lg:h-[18px] shrink-0" />
            <span className={`${isCollapsed ? 'lg:hidden' : ''}`}>Settings</span>
          </button>
        </div>
      </aside>
    </>
  );
}
